import {
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { IconButton } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { Breadcrumb } from 'src/components/Breadcrumb';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import GridData from 'src/components/GridData/GridData';
import { TableHeader, TABLE_HEADER_ACTION_SX } from 'src/components/TableHeader/TableHeader';
import PrivateLayoutView from 'src/Layout/PrivateLayout/PrivateLayoutView';
import {
  firstLetterCamelCase,
  getDateTimeUSAFormat,
} from 'src/constants/helpers';
import { useKeywordsViewController } from '../../controllers';
import { CreateKeywordFormDialog } from '../CreateKeywordForm';
import { CreateQRCodeModal } from 'src/components/CreateQRCodeModal';
import { useCompanyPhoneApi, CompanyPhone } from 'src/modules/companies/api/CompanyPhoneApi';
import { useAppSelector } from 'src/store';
import { getCurrentCompanyId } from 'src/modules/companies/slices/companiesSlice';
import { Button } from 'src/UIKit';

export const KeywordsView = () => {
  const {
    isPageLoading,
    keywordsFilter,
    keywordList,
    changeKeywordsSearchFilter,
    changeKeywordsSorting,
    changeKeywordsPagination,
    removeKeyword,
    createKeywordFormDialogController,
  } = useKeywordsViewController();
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();
  
  const [qrCodeModal, setQrCodeModal] = useState<{
    open: boolean;
    keyword: { id: string; name: string } | null;
  }>({
    open: false,
    keyword: null,
  });

  const [companyPhones, setCompanyPhones] = useState<CompanyPhone[]>([]);
  const [phonesLoading, setPhonesLoading] = useState(true);
  
  const currentCompanyId = useAppSelector(getCurrentCompanyId);
  const companyPhoneApi = useCompanyPhoneApi();

  useEffect(() => {
    const loadCompanyPhones = async () => {
      if (!currentCompanyId) return;
      
      setPhonesLoading(true);
      try {
        const response = await companyPhoneApi.getCompanyPhones(currentCompanyId);
        setCompanyPhones(response.data);
      } catch (error) {
        console.error('Failed to load company phones:', error);
        setCompanyPhones([]);
      } finally {
        setPhonesLoading(false);
      }
    };

    loadCompanyPhones();
  }, [currentCompanyId]);

  const handleOpenQrCode = (keyword: { id: string; name: string }) => {
    setQrCodeModal({
      open: true,
      keyword,
    });
  };

  const handleCloseQrCode = () => {
    setQrCodeModal({
      open: false,
      keyword: null,
    });
  };

  return (
    <>
      <ConfirmationDialog />

      <CreateKeywordFormDialog {...createKeywordFormDialogController} />
      
      <CreateQRCodeModal 
        open={qrCodeModal.open}
        onClose={handleCloseQrCode}
        keyword={qrCodeModal.keyword}
      />

      <>
        <PrivateLayoutView
          title={
            <Breadcrumb items={[{ label: 'Keywords', isActive: true }]} />
          }
        >
          <GridData
            header={
              <TableHeader
                searchValue={keywordsFilter.query}
                onSearchChange={changeKeywordsSearchFilter}
                actions={
                  <Button
                    onClick={() => createKeywordFormDialogController.open()}
                    variant="contained"
                    sx={TABLE_HEADER_ACTION_SX}
                  >
                    Create Keyword
                  </Button>
                }
              />
            }
            isLoading={isPageLoading}
            rows={keywordList?.items}
            columns={[
              {
                field: "name",
                headerName: "Keyword",
                renderCell: (r) => {
                  return firstLetterCamelCase(r.row.name);
                },
                minWidth: 200,
                editable: false,
                sortable: true,
                flex: 1,
              },
              {
                field: "type",
                headerName: "Type",
                minWidth: 100,
                editable: false,
                sortable: true,
                flex: 1,
              },
              {
                field: "campaigns",
                headerName: "Campaigns",
                renderCell: (r) => {
                  if (r.row.campaigns) {
                    return r.row.campaigns
                      .slice(0, 3)
                      .map((c) => c.name)
                      .join(", ");
                  }

                  return "-";
                },
                minWidth: 300,
                editable: false,
                sortable: false,
                flex: 2,
              },
              {
                field: "dateCreated",
                headerName: "Created",
                renderCell: (r) => {
                  return getDateTimeUSAFormat(new Date(r.row.dateCreated));
                },
                minWidth: 100,
                editable: false,
                sortable: true,
                flex: 1,
              },
            ]}
            renderAction={(r) => {
              if (r.type === "default") {
                return null;
              }

              return (
                <>
                  <IconButton size="small" color="primary" onClick={() => createKeywordFormDialogController.open(r)}>
                    <EditIcon fontSize="inherit" />
                  </IconButton>

                  <IconButton 
                    size="small" 
                    color="primary"
                    onClick={() => companyPhones.length > 0 && handleOpenQrCode({ id: r.id, name: r.name })}
                    title={companyPhones.length > 0 ? "Generate QR Code" : "Add a phone number to generate QR codes"}
                    disabled={companyPhones.length === 0 || phonesLoading}
                  >
                    <QrCode2Icon fontSize="inherit" />
                  </IconButton>

                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() =>
                      openDialog({
                        title: `Delete "${r.name}"`,
                        content:
                          "By deleting this keyword it will be removed from all campaigns that it is currently associated with. Are you sure you want to delete this keyword?",
                        onConfirm: () => removeKeyword(r.id),
                      })
                    }
                  >
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </>
              );
            }}
            sorting={{
              value: keywordsFilter.sort,
              onChange: changeKeywordsSorting,
            }}
            pagination={{
              page: keywordsFilter.page,
              pageSize: keywordsFilter.limit,
              totalCount: keywordList?.count,
              onChange: changeKeywordsPagination,
            }}
          />
        </PrivateLayoutView>
      </>
    </>
  );
};
